import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useStores} from "../../stores";
import {
    Autocomplete,
    autocompleteClasses,
    Checkbox,
    CircularProgress,
    Grid,
    Popper,
    TextField,
    Typography
} from "@mui/material";
import {CheckBox, CheckBoxOutlineBlank} from "@mui/icons-material";
import {styled} from "@mui/material/styles";
import {observer} from "mobx-react-lite";
import {runInAction} from "mobx";
import type {Option} from "../../stores/managers/BagAdvancedInsightsManager";

const WIDE_SEARCH_BAR_CUTOFF = 25;
const EXTRA_WIDE_SEARCH_BAR_CUTOFF = 50;

const StyledPopper = styled(Popper)({
    [`& .${autocompleteClasses.listbox}`]: {
        boxSizing: 'border-box',
        '& ul': {
            padding: 0,
            margin: 0,
        },
        'li > div': {
            marginLeft: 8,
        }
    },
});

interface Props {
    wrapperClassName?: string,
    limitTags?: number,
    label: string,
    noOptionsText: string,
}

export const SearchCAddressAutocomplete: React.FC<Props> = observer(({
                                                                         wrapperClassName,
                                                                         limitTags,
                                                                         label,
                                                                         noOptionsText,
                                                                     }) => {
    const {bagStore} = useStores();
    const inputRef = useRef<HTMLInputElement>();

    const [isOpen, setIsOpen] = useState(false);

    /**
     * Limited list of 100 search results
     */
    const [isLoadingSearch, setIsLoadingSearch] = useState(false);
    /**
     * For triggering a selection of the search results that arrive
     */
    const [selectNextSearchTerm, setSelectNextSearchTerm] = useState(false);

    /**
     * This is needed to be memoized or stored in a state
     * https://github.com/mui/material-ui/issues/32425
     */
    const selectedSuppliersHash: string = bagStore.advanced.filters.c_address ? JSON.stringify(bagStore.advanced.filters.c_address) : '';
    const values: Option[] = useMemo(() => {
        if (!bagStore.advanced.filters.c_address) return [];
        return bagStore.advanced.filters.c_address.map(v =>
                bagStore.advanced.searchCAddressResult?.find(s => s.value === v) || bagStore.advanced.searchCAddressCachedOptions.find(s => s.value === v) || ({
                    value: v,
                    label: v,
                    subLabel: '',
                })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSuppliersHash])

    const disableFilter = () => {
        bagStore.advanced.setSearchCAddressCachedOptions([])
        bagStore.advanced.CAddressFilterChanged([])
        setIsOpen(false)
        bagStore.advanced.setSearchCAddressSearchTerm('')
        runInAction(() => {
            bagStore.advanced.searchCAddressResult = [] //not sure if needed
        })
        setIsLoadingSearch(false)
        setSelectNextSearchTerm(false)
        inputRef.current?.blur();
    }

    const pickAllResultOptions = () => {
        // Select all from searchResults
        const searchResultsOptionValues = bagStore.advanced.searchCAddressResult?.map(({value}) => value);
        if (!bagStore.advanced.searchCAddressResult) {
            return
        }
        bagStore.advanced.setSearchCAddressCachedOptions([...bagStore.advanced.searchCAddressResult]);
        // Propagate the change
        bagStore.advanced.CAddressFilterChanged(searchResultsOptionValues);

        // Reset focus
        setIsOpen(false);
        bagStore.advanced.setSearchCAddressSearchTerm('')
        inputRef.current?.blur();
    }

    useEffect(() => {
        const subscription = bagStore.advanced.searchCAddressPipe.subscribe();
        return () => {
            subscription.unsubscribe();
        };
    }, []);

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.code === 'Enter') {
            if (bagStore.advanced.searchCAddressSearchTerm === '') {
                // We will select all supplier, so we simply disable the filter
                disableFilter()
            } else {
                if (!isLoadingSearch) {
                    // Enter is pressed, just pick everything that is now in the view
                    setSelectNextSearchTerm(false);
                    pickAllResultOptions();
                } else {
                    // Pick everything as soon as the loading is done
                    setSelectNextSearchTerm(true);
                }
            }
            e.preventDefault();
        }
    }

    useEffect(() => {
        if (selectNextSearchTerm && !isLoadingSearch) {
            setSelectNextSearchTerm(false);
            pickAllResultOptions();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bagStore.advanced.searchCAddressPipe, isLoadingSearch, selectNextSearchTerm]);

    const isWide = bagStore.advanced.filters.c_address && bagStore.advanced.filters.c_address?.length >= WIDE_SEARCH_BAR_CUTOFF;
    const isExtraWide = bagStore.advanced.filters.c_address && bagStore.advanced.filters.c_address?.length >= EXTRA_WIDE_SEARCH_BAR_CUTOFF;
    const className = (wrapperClassName ? `${wrapperClassName}` : '')
        + (isWide ? ' wide' : '')
        + (isExtraWide ? ' extra-wide' : '')
    return (
        <div className={className}>
            <Autocomplete
                multiple
                limitTags={limitTags ?? 3}
                freeSolo
                value={values}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                options={bagStore.advanced.searchCAddressResult || []}
                filterOptions={x => x}
                noOptionsText={noOptionsText}
                disableCloseOnSelect
                open={isOpen}
                onOpen={() => setIsOpen(true)}
                onClose={() => setIsOpen(false)}
                renderOption={(props, option, {selected}) =>
                    <li {...props} key={option.value}>
                        <Checkbox
                            icon={<CheckBoxOutlineBlank fontSize="small"/>}
                            checkedIcon={<CheckBox fontSize="small"/>}
                            checked={selected}
                        />

                        <Grid container>
                            <Grid size={12}>
                                <Typography>
                                    {option.label}
                                    {option.subLabel && <>
                                        <br/>
                                        <small>{option.subLabel}</small>
                                    </>}
                                </Typography>
                            </Grid>
                        </Grid>
                    </li>
                }
                onChange={(event, options, reason) => {
                    if (event.type === 'keydown' && (event as any as KeyboardEvent).code === 'Enter') return;
                    const optionValues = options.map(o => typeof o === 'string' ? o : o.value)
                    switch (reason) {
                        case 'clear':
                            // setCachedOptions([])
                            bagStore.advanced.CAddressFilterChanged([])
                            break
                        case "removeOption":
                            bagStore.advanced.CAddressFilterChanged(optionValues)
                            break
                        case 'selectOption':
                            // TODO: is this needed?
                            // setCachedOptions([optionValues?])
                            bagStore.advanced.setSearchCAddressSearchTerm('')
                            bagStore.advanced.CAddressFilterChanged(optionValues)
                            break
                        case 'createOption':
                            break
                    }
                }}
                onFocus={() => {
                    bagStore.advanced.setSearchCAddressSearchTerm(bagStore.advanced.searchCAddressSearchTerm)
                }}
                inputValue={bagStore.advanced.searchCAddressSearchTerm}
                onInputChange={(_, value, reason) => {
                    switch (reason) {
                        case 'input':
                            runInAction(() => {
                                bagStore.advanced.setSearchCAddressSearchTerm(value)
                            });
                            break;
                        case 'clear':
                            runInAction(() => {
                                bagStore.advanced.setSearchCAddressSearchTerm('')
                            });
                            break;
                    }
                }}
                renderInput={(params) => {
                    return (
                        <TextField
                            {...params}
                            label={label}
                            placeholder={values.length > 0 ? '' : 'All'}
                            onKeyDown={e => onKeyDown(e as any)}
                            InputProps={{
                                ...params.InputProps,
                                inputRef: inputRef,
                                endAdornment: (
                                    <>
                                        {isLoadingSearch && <CircularProgress color="inherit" size={20}/>}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    );
                }}
                PopperComponent={StyledPopper}
            />
        </div>
    );
})
